<template>
  <div class="custom-wrapper">
    <div class="container">
      <div class="custom-info">
        <p>
          {{ parseTranslation(TITLE_HOME) }} >

          <span>{{ parseTranslation(CUSTOM_LAYOUT) }}</span>
        </p>

        <div>
          <h1>{{ parseTranslation(CUSTOM_LAYOUT) }}</h1>
        </div>
      </div>

      <div class="title">
        <h2>{{ fileName }}</h2>

        <router-link :to="{ name: 'scheme' }">
          <BackArrowIcon /> {{ parseTranslation(BACK) }}</router-link
        >
      </div>

      <div class="form">
        <div>
          <div>
            <p class="label">{{ parseTranslation(CHOOSE_COLOR) }}:</p>

            <div>
              <button
                v-for="questionTheme in themeExtractors"
                :key="questionTheme.theme"
                :class="{ active: theme === questionTheme.theme }"
                class="palette"
                @click="theme = questionTheme.theme"
              >
                <img :src="getImgUrl(questionTheme.theme)" :alt="questionTheme.theme" />
              </button>
            </div>
          </div>

          <div>
            <p class="label">{{ parseTranslation(UPLOAD_LOGO) }}:</p>

            <div>
              <label for="logo-input">
                <Loader v-if="!image && loadingMedia" color="#0695fd" :size="30" />

                <div v-else-if="!loadingMedia && !image" class="no-image">
                  <ImageIcon />

                  <span>{{ translations[language][UPLOAD_LOGO] }}</span>
                </div>

                <img v-else-if="!loadingMedia && image" :src="image" alt="upload image" />
              </label>

              <input
                @change="event => extractFile(event.target)"
                accept="image/*"
                id="logo-input"
                type="file"
              />
            </div>
          </div>
        </div>

        <div>
          <div class="switch">
            {{ parseTranslation(TOGGLE_PROGRESS_BAR) }}:

            <div :class="{ active: progressBar }">
              <button :class="{ active: !progressBar }" @click="progressBar = false">
                {{ parseTranslation(NO) }}
              </button>

              <button :class="{ active: progressBar }" @click="progressBar = true">
                {{ parseTranslation(YES) }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="preview">
        <p class="label">
          {{ parseTranslation(PREVIEW) }}
        </p>

        <div>
          <div
            class="preview-image"
            v-for="questionTheme in themeExtractors"
            :key="questionTheme.theme"
          >
            <img
              :class="{ active: theme === questionTheme.theme && progressBar }"
              :src="getImgUrl(questionTheme.previewWithProgressBar)"
              :alt="`Preview ${questionTheme.theme}`"
            />

            <img
              :class="{ active: theme === questionTheme.theme && !progressBar }"
              :src="getImgUrl(questionTheme.preview)"
              :alt="`Preview ${questionTheme.theme}`"
            />
          </div>

          <div class="logo">
            <Loader v-if="!image && loadingMedia" color="#0695fd" :size="30" />

            <p v-else-if="!image && !loadingMedia">Logo</p>

            <img v-else-if="!loadingMedia && image" :src="image" alt="Logo" />
          </div>
        </div>
      </div>

      <div class="button">
        <button @click="save">
          <span v-if="!loading">{{ parseTranslation(SAVE) }}</span>

          <span v-else>{{ parseTranslation(LOADING) }}...</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

import { uploadFile } from '@/services/api'

import ImageIcon from '@/components/imgs/ImageIcon'
import BackArrowIcon from '@/components/imgs/BackArrow.vue'

import Loader from '@/components/loading/Loader-3'

import * as translationCustomization from './index.translations'

const { default: translations, ...translationKeys } = translationCustomization

export default {
  name: 'Customization',

  components: { ImageIcon, BackArrowIcon, Loader },

  filters: {
    maxLength(value) {
      if (value.length > 20) {
        return (
          value
            .split('')
            .splice(0, 20)
            .join('') + '...'
        )
      } else return value
    },
  },

  data() {
    return {
      translations,
      ...translationKeys,
      image: null,
      theme: 'orange',
      progressBar: true,
      loading: false,
      loadingMedia: false,
    }
  },

  computed: {
    fileName() {
      return this.$store.getters.survey.name
    },

    themeExtractors() {
      const themeExtractors = [
        {
          theme: 'orange',
          previewWithProgressBar: 'preview-orange-with-progress-bar',
          preview: 'preview-orange-no-progress-bar',
        },

        {
          theme: 'gray',
          previewWithProgressBar: 'preview-gray-with-progress-bar',
          preview: 'preview-gray-no-progress-bar',
        },

        {
          theme: 'green',
          previewWithProgressBar: 'preview-green-with-progress-bar',
          preview: 'preview-green-no-progress-bar',
        },

        {
          theme: 'yellow',
          previewWithProgressBar: 'preview-yellow-with-progress-bar',
          preview: 'preview-yellow-no-progress-bar',
        },

        {
          theme: 'blue',
          previewWithProgressBar: 'preview-blue-with-progress-bar',
          preview: 'preview-blue-no-progress-bar',
        },

        {
          theme: 'pink',
          previewWithProgressBar: 'preview-pink-with-progress-bar',
          preview: 'preview-pink-no-progress-bar',
        },
      ]

      return themeExtractors
    },
  },

  methods: {
    ...mapActions(['sendMessage', 'executeSurveyAPICall']),

    getImgUrl(stat) {
      return require(`@/assets/imgs/${stat}.png`)
    },

    async extractFile({ files }) {
      if (this.loadingMedia === true) return

      this.loadingMedia = true

      const [file] = files || []

      if (!file || !file?.type.startsWith('image')) {
        return (this.loadingMedia = false)
      }

      if (file !== 'string') {
        try {
          const { url } = await uploadFile(file)

          this.image = url

          this.loadingMedia = false
        } catch (err) {
          this.sendMessage(this.translations[this.language][this.SAVE_ERROR])

          this.loadingMedia = false
        }
      }
    },

    async save() {
      if (this.loading) return

      this.loading = true

      const updatedSurvey = await this.executeSurveyAPICall({
        path: 'survey',
        method: 'PUT',
        payload: {
          survey_id: this.$store.getters.survey.id_,
          step: 'scheme',
          theme: {
            logo: this.image || null,
            palette: this.theme,
            progressbar: this.progressBar,
          },
        },
      })

      this.loading = false

      if (!updatedSurvey) return

      this.sendMessage(this.translations[this.language][this.SURVEY_SAVED])

      await this.$router.push({ name: 'scheme' })
    },
  },

  created() {
    if (this.$store.getters.survey.theme) {
      if (this.$store.getters.survey.theme.logo) {
        this.image = this.$store.getters.survey.theme.logo
      }

      this.theme = this.$store.getters.survey.theme.palette
      this.progressBar = this.$store.getters.survey.theme.progressbar
    }
    window.scrollTo({ top: 0 })
  },
}
</script>

<style lang="scss" scoped>
.custom-info {
  padding: 30px 0 20px;
  border-bottom: 1px solid $white;
  h1 {
    @include tipography(26);
    font-weight: bold;
  }
  div {
    margin-top: 10px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    p {
      @include tipography(16);
      margin: 3px 0 0 15px;
      @include d(s) {
        margin: 10px 0 0;
      }
    }
  }
  > p {
    @include tipography(14);
    color: $gray-2;
    span {
      color: $white;
    }
  }
  .light & {
    border-bottom: 1px solid $gray-5;
    h1,
    span {
      color: $gray-6;
    }
    p {
      color: $gray;
    }
  }
}

.label {
  @include tipography(16);
  .light & {
    color: $gray-6;
  }
}

.form {
  > div {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    margin-top: 40px;

    div {
      &:nth-of-type(1) {
        p {
          margin-bottom: 10px;
        }

        .palette {
          background: none;
          border: none;
          cursor: pointer;
          border-radius: 50%;
          border: none;
          width: 80px;
          height: 80px;
          border: 3px solid rgba(0, 0, 0, 0);
          transition: all 1s ease;
          padding: 3px;
          margin-right: 15px;
          img {
            width: 100%;
          }
          &:focus,
          &.active {
            outline: none;
            border: 3px solid rgba(200, 200, 200, 0.8);
          }
        }
      }

      &:nth-of-type(2) {
        p {
          margin-bottom: 10px;
        }

        label {
          padding: 5px;
          position: relative;
          @include flexMiddle;
          cursor: pointer;
          border: 1px solid $gray-5;
          border-radius: 8px;
          background: $main-3;
          width: 300px;
          height: 170px;

          .no-image {
            @include flexMiddle;
            flex-direction: column;

            svg {
              @include boxSizing(45);

              margin-bottom: 10px;
            }

            span {
              @include tipography(15);
              font-weight: bold;
              text-transform: uppercase;
              text-align: center;
              letter-spacing: 0.1em;
              line-height: 18px;

              .light & {
                color: $gray-6;
              }
            }
          }

          img {
            width: 100%;
            height: 100%;
            object-fit: contain;
            border-radius: 8px;
          }

          @media (max-width: 400px) {
            width: 200px;
            height: 113px;
          }

          @media (max-width: 250px) {
            width: 150px;
            height: 85px;
          }
        }

        input {
          display: none;
        }

        .light & {
          label {
            background: $gray-light-3;
          }
        }
      }
    }
  }
}

.preview {
  margin-top: 60px;

  > div {
    position: relative;

    .preview-image {
      position: relative;

      img {
        animation: show 0.3s linear;
        display: none;
        width: 100%;
        height: 100%;

        &.active {
          display: block;
        }
      }
    }

    .logo {
      @include flexMiddle;
      border-radius: 3px;
      width: 100px;
      height: 57px;
      position: absolute;
      top: 48px;
      left: 99px;

      p {
        @include tipography(40);
        font-weight: bold;
        text-transform: uppercase;
        letter-spacing: 0.2rem;
      }

      img {
        border-radius: 3px;
        width: 100%;
        height: 100%;
        object-fit: contain;
      }

      .light & {
        p {
          color: $main;
        }
      }
    }
  }
}

.button {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;

  button {
    @include tipography(14);
    border-radius: 3px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    font-weight: bold;
    border: 1px solid $blue;
    padding: 10px 20px;
    background: $blue;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: $blue-2;
      transform: scale(1.01);
    }

    &:focus {
      outline: none;
    }
  }
}

.title {
  margin-top: 30px;

  h2 {
    @include tipography(26);
    text-transform: capitalize;
    margin-bottom: 20px;
  }

  a {
    display: inline-flex;
    align-items: center;
    padding: 5px 20px;
    background: none;
    border: 2px solid $blue;
    border-radius: 3px;
    cursor: pointer;
    @include tipography(12);
    text-transform: uppercase;
    letter-spacing: 1px;
    white-space: nowrap;
    transition: all 0.3 ease;

    svg {
      margin-right: 8px;
    }

    &:hover {
      transform: scale(1.01);
    }

    &:focus {
      outline: none;
    }
  }

  .light & {
    h2 {
      color: $main;
    }

    a {
      color: $gray-6;
    }
  }
}

.switch {
  display: flex;
  align-items: center;
  margin-top: 40px;
  @include tipography(14);
  .light & {
    color: $gray-2;
  }
  div {
    margin-left: 20px;
    border: 1px solid $gray-5;
    border-radius: 50px;
    position: relative;
    transition: all 0.3s ease;
    &::after {
      content: 'yes';
      background: $green-2;
      border-radius: 50px;
      padding: 6px 30px;
      position: absolute;
      left: 3px;
      top: 4px;
      color: $transparent;
      transition: all 0.3s ease;
    }
    &.active {
      &::after {
        left: calc(100% - 83px);
      }
    }
    button {
      position: relative;
      z-index: 2;
      padding: 8px 30px;
      background: none;
      border: none;
      cursor: pointer;
      border-radius: 50px;
      transition: all 0.3s ease;
      @include tipography(14);
      font-weight: bold;
      text-transform: uppercase;

      &.active {
        color: $white;
        &:hover {
          color: $gray-light-2;
        }
      }
    }
  }

  .light & {
    div {
      button {
        color: $gray-2;

        &:hover {
          color: $gray-8;
        }

        &.active {
          color: $white;
        }
      }
    }
  }
}

@media (max-width: 1144.98px) {
  .preview {
    > div {
      .logo {
        display: none;
      }
    }
  }
}

@media (max-width: 826.98px) {
  .form {
    > div {
      div {
        &:nth-of-type(2) {
          margin-top: 30px;
        }
      }
    }
  }
}
</style>
